/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { LaunchSummaryDto, SubscriptionDto, UserDto } from '@/commons/dtos';
import { AribaMapName } from '@/commons';
import axiosInstance from '@/utils/axiosInstance';
import * as storageUtils from '@/utils/storageUtils';

const imageMap: Record<string, string> = {
    sourcingevent: '/images/sourcing_event.svg',
    sourcingeventcontent: '/images/sourcing_event.svg',
    sourcingproject: '/images/sourcing_project.svg',
    projectcreation: '/images/project_creation.svg',
    projectdeletion: '/images/project_deletion.svg',
    eventmessaging: '/images/event_messaging.svg',
    taskstarts: '/images/message.svg',
    projectstarts: '/images/project_start_email.svg',
    ownergroup: '/images/owner_group.svg',
    taskowneremail: '/images/notify_project_create.svg',
    approval: '/images/approval.svg',
    default: '/images/add_user.svg',
};

const skuToInfoMap: Record<number, any> = {
    8007883: {
        title: 'SAP Ariba Sourcing',
        subTitle: 'SAP Ariba Sourcing',
        imgPath: 'BalloonSky',
        altProp: 'BalloonSky',
    },
    8015622: {
        title: 'SAP Ariba Sourcing, Base Edition',
        subTitle: 'SAP Ariba Sourcing, base edition',
        imgPath: 'BalloonSky',
        altProp: 'BalloonSky',
    },
    8007889: {
        title: 'SAP Ariba Supplier Lifecycle and Performance',
        subTitle: 'SAP Ariba Supplier Lifecycle and Performance',
        imgPath: 'BalloonSky',
        altProp: 'BalloonSky',
    },
    1: {
        title: 'SAP Business Network for Procurement',
        subTitle: 'Automation of Source-to-Pay with SAP Business Network (42K)',
        imgPath: 'BalloonSky',
        altProp: 'BalloonSky',
    },
    8007885: {
        title: 'SAP Ariba Contracts',
        subTitle: 'SAP Ariba Contracts',
        imgPath: 'BeforeSearch',
        altProp: 'BeforeSearch',
    },
    8014432: {
        title: 'SAP Ariba Supplier Risk, Base Edition',
        subTitle: 'SAP Ariba Supplier Risk, base edition',
        imgPath: 'BeforeSearch',
        altProp: 'BeforeSearch',
    },
};

export const validateUserDtls = (users: UserDto[]) => {
    const failedUsers: UserDto[] = [];

    let notProvided: string[] = [];
    let notValid: string[] = [];

    users.forEach(user => {
        notProvided = [];
        notValid = [];
        if (!user.givenName) {
            notProvided.push('First Name');
        }

        if (!user.familyName) {
            notProvided.push('Last Name');
        }

        if (!user.userName) {
            notProvided.push('User Name');
        }

        if (!user.emailId) {
            notProvided.push('Business Email id');
        }

        if (!validateEmailId(user.emailId)) {
            notValid.push('Business Email id');
        }

        if (!user.role) {
            notProvided.push('User Role');
        }

        let message = '';

        if (notProvided.length > 0 && notValid.length > 0) {
            message = `Please provide ${notProvided.join(', ')} and valid ${notValid.join(', ')}.`;
        } else if (notProvided.length > 0) {
            message = `Please provide ${notProvided.join(', ')}.`;
        } else if (notValid.length > 0) {
            message = `Please provide valid ${notValid.join(', ')}.`;
        }

        if (message) {
            failedUsers.push({
                ...user,
                errorMsg: message,
            });
        }
    });

    return failedUsers;
};

export function validateEmailId(value: string | undefined): boolean {
    if (!value) {
        return false;
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(value);
}
export function validateFirstName(value: string | undefined): boolean {
    if (!value) {
        return false;
    }
    const FirstRegex = /^[A-Za-z]+$/;
    return FirstRegex.test(value) && value.length <= 22;
}
export function validateLastName(value: string | undefined): boolean {
    if (!value) {
        return false;
    }
    const LastRegex = /^[A-Za-z]+$/;
    return LastRegex.test(value) && value.length <= 22;
}

interface ValidUser {
    [key: string]: string | number;
}

export function getValidUsers(users: ValidUser[]): ValidUser[] {
    const validUsers = users.filter((user: ValidUser) => {
        for (const key in user) {
            if (!['runId', 'index', 'createdBy'].includes(key) && user[key] !== null && user[key] !== '' && user[key] !== '-1') return true;
        }
        return false;
    });

    return validUsers;
}

interface Param {
    key: string;
    value: string | number | boolean;
}

export function generateQueryParams(paramsArray: Param[]) {
    const queryParams = paramsArray.map(param => {
        const { key, value } = param;
        return `${key}=${value}`;
    });
    if (queryParams.length > 0) {
        return `?${queryParams.join('&')}`;
    }
    return '';
}

export function getImagePathFromStep(stepName: string): string {
    stepName = stepName?.replace(/\s/g, '')?.toLowerCase();
    return imageMap[stepName];
}

export function validateUUIDv4(uuid: string | undefined): uuid is string {
    if (!uuid) {
        return false;
    }
    const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidV4Regex.test(uuid);
}

export function getProductNameBySKUID(skuId: number) {
    return skuToInfoMap[skuId];
}

export function getLaunchedProducts(productSubscriptions?: SubscriptionDto[]) {
    if (!productSubscriptions) return [];
    const launchedProducts = productSubscriptions
        .filter((product: SubscriptionDto) => {
            return product.launchTriggered && product.productDto.sku !== 1 && product.productDto.sku !== 2;
        })
        .map((product: SubscriptionDto) => ({
            id: product.flowId,
            label: product.productDto.name,
        }));
    return launchedProducts;
}

export function getProductByFlowId(flowId: string, productSubscriptions?: SubscriptionDto[]) {
    return productSubscriptions?.find(product => {
        return product.flowId === flowId;
    });
}

export function mapUIStatus(status: LaunchSummaryDto['status']) {
    if (status === 'COMPLETED_WITH_ERROR') {
        return 'COMPLETED';
    }
    return status;
}

export const formatText = (text: string): string => {
    const words = text.split('_');
    const formattedWords = words.map(word => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
    const formattedText = formattedWords.join(' ');
    return formattedText;
};

export const formatDate = (inputDateString: string) => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = new Date(inputDateString);
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZone: userTimeZone,
    };
    return date.toLocaleString('en-US', options);
};

/**
 * Check for empty array.
 * @param {any} array - The value to check.
 * @returns {boolean} - Returns true if the value is an empty array, false otherwise.
 */
export function checkIsEmptyArray(array: any): boolean {
    return Array.isArray(array) && array.length === 0;
}

/**
 * Check for empty object.
 * @param {any} obj - The value to check.
 * @returns {boolean} - Returns true if the value is an empty object, false otherwise.
 */
export function checkIsEmptyObject(obj: any): boolean {
    //explicitly check that object is not null, undefined, or """ (untruthy values), is a plain object
    if (obj && typeof obj === 'object' && obj.constructor === Object) {
        return Object.keys(obj).length === 0;
    }
    return false;
}

/**
 * Check for object have empty value.
 * @param {any} obj - The value to check.
 * @returns {boolean} - Returns true if the value is an empty object, false otherwise.
 */
export function checkEmptyValueInObject(obj: any): boolean {
    const parsedObj = JSON.parse(obj);
    const filteredKeys = Object.keys(parsedObj).filter(key => key !== 'others');
    const values = filteredKeys.map(key => parsedObj[key]);

    return values.some(value => {
        return isEmpty(value);
    });
}

/**
 * Check for falsy value.
 * @param {any} value - The value to check.
 * @returns {boolean} - Returns true if the value is falsy, false otherwise.
 */
export function checkIsFalsyValue(value: any): boolean {
    return !value;
}

/**
 * Check for empty array, empty object, or falsy value.
 * @param {any} value - The value to check.
 * @returns {boolean} - Returns true if the value is falsy, is an empty array, or an empty object. False otherwise.
 */
export function isEmpty(value: any): boolean {
    return checkIsFalsyValue(value) || checkIsEmptyArray(value) || checkIsEmptyObject(value);
}

export function removeBrowserSuffix(locale: string): string {
    return locale.replace(/_BROWSER$/, '');
}

export const isJSONObject = (text: string): boolean => {
    try {
        const parsed = JSON.parse(text);
        return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
    } catch (e) {
        return false;
    }
};

export const JSONObjectDeepEqual = (a: any, b: any): boolean => {
    if (a === b) return true;

    if (typeof a !== typeof b || a === null || b === null) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        return a.every(itemA => b.some(itemB => JSONObjectDeepEqual(itemA, itemB)));
    }

    if (typeof a === 'object') {
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        if (keysA.length !== keysB.length) return false;
        return keysA.every(key => JSONObjectDeepEqual(a[key], b[key]));
    }

    return false;
};

/**
 * Utility function for conditionally joining CSS class names together
 *
 * @param {...(string|Object|Array)} args - Class names or conditional objects
 * @returns {string} - Combined className string
 *
 * Example usage:
 * classNames('base-class', { 'active': isActive, 'disabled': isDisabled }, someCondition && 'conditional-class')
 */

type ClsxArgument =
    | string
    | number
    | boolean
    | undefined
    | null
    | Record<string, null | undefined | boolean>
    | Array<string | number | null | undefined | boolean>;

export const clsx = (...args: ClsxArgument[]): string => {
    const classes: Array<string | number> = [];

    args.forEach(arg => {
        if (!arg) return;

        if (typeof arg === 'string' || typeof arg === 'number') {
            classes.push(arg);
        } else if (Array.isArray(arg)) {
            classes.push(clsx(...arg));
        } else if (typeof arg === 'object') {
            Object.entries(arg).forEach(([key, value]) => {
                if (value) {
                    classes.push(key);
                }
            });
        }
    });

    return classes.join(' ').trim();
};

export function getAribaSkuNameById(skuId: number) {
    const skuName = AribaMapName[skuId];

    if (!skuName) {
        console.error(`SKU ID ${skuId} not found in Ariba SKU mapping.`);
        return null;
    }

    return skuName;
}

export function getUserRoleTranslationKeyByRoleValue(roleValue: string) {
    return roleValue
        .split(' ')
        .map(word => word.toUpperCase())
        .join('_')
        .toString();
}

export function createNumericKeyObject(length: number): Record<string, string> {
    const obj: Record<string, string> = {};
    for (let i = 1; i <= length; i++) {
        obj[i.toString()] = '';
    }
    return obj;
}

export const refreshUserCredentials = async () => {
    await axiosInstance.get(`/logout`);
    storageUtils.removeItem(['refreshToken', 'token', 'user', 'code', 'state']);
    window.location.href = `/oauth2/authorization/ias`;
};
